import { useHistory, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import PermissionGroup from "../role/PermissionGroup";
import { Alert, Button, Card, Form, Input, InputNumber, Select, Switch } from "antd";
import RoleSelectBox from "../role/RoleSelectBox";
import { t } from "i18next";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import FirmSelectList from "../firm/FirmSelectList";
import * as qz from "qz-tray";
import CashRegisterSelectList from "../cash-register/CashRegisterSelectList";
import { readStartPage, START_PAGES } from "../../core/start-pages";
import usePermissionGuard from "../role/usePermissionGuard";

/**
 * Hem tam sayfa (route) hem de modal içinde kullanılır.
 * @param userId   Modal kullanımında düzenlenecek kullanıcı; verilmezse route parametresi okunur.
 * @param embedded true ise sayfa iskeleti (SimpleLayout + Card) çizilmez, yalnız form döner.
 * @param onSaved  Kayıt başarılı olduğunda çağrılır (modalı kapatıp listeyi tazelemek için).
 */
export default function UserCreateEdit({ userId: userIdProp, embedded = false, onSaved }) {
    const history = useHistory();
    const formRef = useRef();
    const params = useParams();
    const userId = userIdProp ?? params.userId;

    const [user, setUser] = useState({
        permissionIdList: []
    });
    // Sunucudan gelen (kaydedilmiş) hali: kendinden fazla yetkili kullanıcı düzenlenemez.
    const [loadedUser, setLoadedUser] = useState(null);
    const { canManageUser } = usePermissionGuard();
    const locked = !!userId && !!loadedUser && !canManageUser(loadedUser);

    // 2FA yalnız kanalı olan kullanıcıda açılabilir: telefonu da e-postası da olmayan kullanıcı
    // 2FA açıkken kod alamaz ve HİÇ giriş yapamaz (sunucu girişi reddediyor). Anahtar buna göre pasifleşir.
    const [contact, setContact] = useState({ email: '', phone: '' });

    const getUser = async () => {
        if (userId) {
            const res = await ApiService.User.GetUser(userId);
            if (res.success) {
                setUser(res.data);
                setLoadedUser(res.data);
                setContact({ email: res.data.email, phone: res.data.phone })
                formRef.current.setFieldsValue({
                    ...res.data,
                    roleId: res.data.role.id,
                    // Açılış sayfası ayrı bir kolon değil, Settings JSON'unun içinde.
                    startPage: readStartPage(res.data),
                    firmIdList: res.data?.firmUsers?.map(x => x.firmId),
                    cashRegisterList: res.data?.userCashRegisters.map(x => x.cashRegisterId)
                })
            }
        }
    }

    const onValuesChange = (_, formData) => {
        setContact({ email: formData.email, phone: formData.phone })

        // Son kanal da silinirse 2FA kendiliğinden kapanır — kaydetmede hata almak yerine tutarlı kalırız
        if (!formData.email && !formData.phone && formData.mfaEnabled)
            formRef.current.setFieldsValue({ mfaEnabled: false })
    }

    const [selectedPrinter, setSelectedPrinter] = useState(null);
    const [loadingPrinters, setLoadingPrinters] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [printers, setPrinters] = useState([]);

    const listPrinters = async () => {
        try {
            setLoadingPrinters(true);

            if (!qz.websocket.isActive()) {
                await qz.websocket.connect({ verify: false });
            }

            const printerList = await qz.printers.find();
            setPrinters(printerList);

            if (printerList.length > 0 && !selectedPrinter) {
                setSelectedPrinter(printerList[0]);
            }
            setShowPrinterSelect(true);
        } catch (err) {
            alert(t('Could not get the printer list'));
            console.log('Liste hatası->', err);
        } finally {
            setLoadingPrinters(false);
        }
    };

    useEffect(() => {
        getUser();
    }, [userId])

    const save = async () => {
        if (userId) {
            const res = await ApiService.User.EditUser(userId, {
                ...formRef.current.getFieldsValue(),
                permissionIdList: user.permissionIdList
            })

            if (res.success) {
                onSaved && onSaved()
            }
        }
        else {
            const res = await ApiService.User.CreateUser({
                ...formRef.current.getFieldsValue(),
                permissionIdList: user.permissionIdList
            })

            if (res.success) {
                history.push(`/app/system/users/edit/${res.data}`)
            }
        }
    }

    const form = (
        <Form
            layout={'vertical'}
            ref={formRef}
            onFinish={save}
            onValuesChange={onValuesChange}
            initialValues={{ mfaEnabled: true }}
            disabled={locked}
        >
        {locked && (
            <Alert
                type={'warning'}
                showIcon={true}
                style={{ marginBottom: 16 }}
                message={t('This user has permissions you do not have, so you cannot edit them.')}
            />
        )}
        <Form.Item
            label={t('Firm')}
            name={'firmIdList'}
            rules={[{ required: true }]}
        >
            <FirmSelectList
                all={true.toString()}
                style={{ width: '100%' }}
                allowClear={true}
                mode={'multiple'}
            />
        </Form.Item>

        <Form.Item
            label={t('Username')}
            name={'username'}
            rules={[
                { pattern: /^[A-Za-z0-9]+(\.[A-Za-z0-9]+)*$/, message: t('UsernameInvalidChars') },
                { required: true }
            ]}
        >
            <Input disabled={userId} />
        </Form.Item>

        <Form.Item
            label={t('Email')}
            name={'email'}
            rules={[{ required: true }]}
        >
            <Input />
        </Form.Item>

        <Form.Item
            label={t('Phone')}
            name={'phone'}
            extra={t('Include the country code, e.g. 905321234567. Used for the SMS login code.')}
        >
            <Input />
        </Form.Item>

        {/* İki aşamalı doğrulama — TEK anahtar. Kodu SMS ile mi e-posta ile mi alacağını
            kullanıcı giriş anında seçer; hangi iletişim bilgisi doluysa o seçenek çıkar. */}
        <Form.Item
            label={t('Two-factor authentication')}
            name={'mfaEnabled'}
            valuePropName={'checked'}
            extra={
                !contact.email && !contact.phone
                    ? t('Requires a phone number or an e-mail address')
                    : t('At login the user receives a verification code by SMS or e-mail.')
            }
        >
            <Switch disabled={!contact.email && !contact.phone} />
        </Form.Item>

        <Form.Item
            label={t('Name')}
            name={'name'}
            rules={[{ required: true }]}
        >
            <Input />
        </Form.Item>

        <Form.Item
            label={t('Password')}
            name={'password'}
            rules={[{ required: !userId }]}
        >
            <Input />
        </Form.Item>

        <Form.Item
            label={t('Cash Registers')}
            name={'cashRegisterList'}
        >
            <CashRegisterSelectList
                mode={'multiple'}
                style={{ width: '100%' }}
                all={true}
            />
        </Form.Item>

        {userId && (
            <Form.Item
                label={t('Enabled')}
                name={'enabled'}
                rules={[{ required: true }]}
            >
                <Select
                    style={{ width: '100%' }}
                    options={[
                        { value: true, label: t('Active') },
                        { value: false, label: t('Passive') }
                    ]}
                />
            </Form.Item>
        )}


        {/*<Form.Item*/}
        {/*    label={t('Discount')}*/}
        {/*    name={'discount'}*/}
        {/*    required={true}*/}
        {/*>*/}
        {/*    <InputNumber />*/}
        {/*</Form.Item>*/}

        <Form.Item
            label={t('Role')}
            name={'roleId'}
            rules={[{ required: true }]}
        >
            <RoleSelectBox />
        </Form.Item>

        {/* Kullanıcı uygulamaya girdiğinde açılan ekran. Boş bırakılırsa izne göre karar
            verilir; seçilen sayfaya yetkisi yoksa da aynı varsayılana düşer. */}
        <Form.Item
            label={t('Start Page')}
            name={'startPage'}
            extra={t('The page shown right after login. If left empty it is chosen by permission.')}
        >
            <Select
                allowClear={true}
                style={{ width: '100%' }}
                placeholder={t('Automatic')}
                options={START_PAGES.map(x => ({ value: x.value, label: t(x.labelKey) }))}
            />
        </Form.Item>
        <Form.Item
            label={t('Printer')}
            name={'printerName'}
        >
            <Select
                style={{ width: '100%' }}
                loading={loadingPrinters}
                onDropdownVisibleChange={(open) => {
                    // Selectbox açıldığında listeyi çek
                    if (open && printers.length === 0) {
                        listPrinters();
                    }
                }}
                placeholder={t('Select Printer')}
                value={selectedPrinter}
                onChange={(val) => setSelectedPrinter(val)}
            >
                {printers.map((printer) => (
                    <Select.Option key={printer} value={printer}>
                        {printer}
                    </Select.Option>
                ))}
            </Select>
        </Form.Item>

        <Form.Item
            label={t('Other Permissions')}
            name={'permissionIdList'}
        >
            <PermissionGroup
                disabled={locked}
                selectedPermissions={user.permissionIdList}
                onChangeSelectedPermissions={(permissions) => {
                    setUser({
                        ...user,
                        permissionIdList: permissions
                    })
                }}
            />
        </Form.Item>
        <br />

        <Form.Item>
            <Button htmlType={'submit'} type={'primary'}>{t('Save')}</Button>
        </Form.Item>
    </Form>
    )

    if (embedded)
        return form;

    return (
        <SimpleLayout>
            <Card
                title={!userId ? t('New User') : `${t('Edit User')} - ${user.name}`}
            >
                {form}
            </Card>
        </SimpleLayout>
    )
}
